import type { CSSProperties, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import { X, MessageSquarePlus, Plus, Pencil, Lock, LockOpen, Globe, FileText, Paperclip, Loader2, Trash2, ArrowRightLeft, Calendar, ArrowRight, User, Mail, Hash, Clock, Activity, MessageSquare, LogOut, Users, Bot, Sparkles } from 'lucide-react'
import { cn } from '../../../utils/cn.js'
import { formatDateTime } from '../../../utils/timezone.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import { useAuthStore } from '../../../store/authStore.js'
import { useChatTags } from '../../../hooks/useChatTags.js'
import { useAssignableAgents } from '../../../hooks/useAssignableAgents.js'
import { useChatNotes, type ChatNote } from '../../../hooks/useChatNotes.js'
import { useUploadAttachment } from '../../../hooks/useUploadAttachment.js'
import { type ChatTransfer } from '../../../hooks/useChatTransfers.js'
import { api } from '../../../api/client.js'
import { getSocket } from '../../../socket/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useConfirmDialog } from '../../../components/ui/ConfirmDialog.js'
import RichTextEditor, { isEmptyHtml } from '../../../components/common/RichTextEditor.js'
import ActionDrawer from '../../../components/conversations/detail/ActionDrawer.js'
import { Avatar, DangerButton, Field, GhostButton, PrimaryButton, R, Section } from '../../../components/conversations/detail/primitives.js'
import type { ChatListEntry } from './ChatConversationList.js'
import { ChatSummaryDialog } from './ChatSummaryDialog.js'
import { SafeHtml } from '../../../components/common/SafeHtml.js'

const ACCEPTED_EXTENSIONS = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.gif,.png,.pdf,.txt,.odt,.ods,.zip,.rar,.mp4,.mov,.avi,.wmv,.flv,.mkv,.webm,.mpeg,.mpg,.m4v'

interface PendingAttachment {
  localId: string
  name: string
  state: 'uploading' | 'uploaded' | 'error'
  attachmentId?: string | undefined
  mimeType?: string | undefined
  previewUrl?: string | undefined
  file?: File | undefined
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '-'
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}


function TagsEditor({ chatId, channel }: { chatId: string; channel: string }): ReactNode {
  const { tags, loading, addTag, removeTag } = useChatTags(chatId, channel)
  const [input, setInput] = useState('')
  const [expanded, setExpanded] = useState(false)
  const MAX = 10
  const tagsToShow = expanded ? tags : tags.slice(0, MAX)
  const more = expanded ? 0 : Math.max(0, tags.length - MAX)

  const handleAdd = (): void => {
    const tag = input.trim()
    if (!tag) return
    setExpanded(true)
    void addTag(tag).then(() => setInput(''))
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {tagsToShow.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-[3px] bg-blue-50 px-2 py-1 text-[10px] font-medium text-[#3366FF]"
          >
            {tag}
            <button
              type="button"
              onClick={() => void removeTag(tag)}
              disabled={loading}
              className="rounded p-0.5 text-[#3366FF]/70 hover:bg-[#3366FF]/10 hover:text-[#3366FF] disabled:opacity-50"
              aria-label={`Remove ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {more > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded-[3px] bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-200"
          >
            +{more} more
          </button>
        )}
        {expanded && tags.length > MAX && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="rounded-[3px] bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-200"
          >
            Show less
          </button>
        )}
      </div>
      <div className="mt-1.5">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleAdd()
            }
          }}
          placeholder="+ Add tag"
          disabled={loading}
          className="h-6 w-28 rounded-[3px] border border-dashed border-slate-300 bg-white px-2 py-1 text-[10px] text-slate-700 placeholder:text-slate-400 focus:border-[#3366FF] focus:outline-none disabled:opacity-50"
        />
      </div>
    </div>
  )
}

function NoteCard({
  note,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onView,
  compact,
}: {
  note: ChatNote
  canEdit?: boolean
  canDelete: boolean
  onEdit?: (note: ChatNote) => void
  onDelete: (noteId: string) => void
  onView?: (note: ChatNote) => void
  compact?: boolean
}): ReactNode {
  const author = note.authorName ?? 'Unknown'
  return (
    <div
      onClick={() => onView?.(note)}
      className={cn('border border-slate-100 bg-white p-3 shadow-sm', onView ? 'cursor-pointer hover:shadow-md' : '', R)}
    >
      <div className="mb-2 flex items-center gap-2">
        <Avatar name={author} size={28} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[11px] font-semibold text-slate-800">{author}</p>
          <p className="text-[10px] text-slate-400">{formatDateTime(note.createdAt)}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {note.isPrivate ? (
            <Lock className="h-3 w-3 shrink-0 text-amber-400" />
          ) : (
            <Globe className="h-3 w-3 shrink-0 text-emerald-400" />
          )}
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onEdit?.(note)
              }}
              className="rounded p-0.5 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
              aria-label="Edit note"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(note.id)
              }}
              className="rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Delete note"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {note.bodyHtml ? (
        <SafeHtml
          className={cn(
            'text-xs leading-relaxed text-slate-600 [&_a]:text-[#3366FF] [&_a]:underline',
            compact ? 'line-clamp-4' : '',
          )}
          html={note.bodyHtml}
        />
      ) : (
        <p className={cn('text-xs leading-relaxed text-slate-600', compact ? 'line-clamp-4 whitespace-pre-wrap' : 'whitespace-pre-wrap')}>
          {note.body}
        </p>
      )}
      {note.attachments && note.attachments.length > 0 && (
        <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
          <Paperclip className="h-3 w-3" />
          <span>{note.attachments.length} {note.attachments.length === 1 ? 'attachment' : 'attachments'}</span>
        </div>
      )}
    </div>
  )
}

function ClientStatus({ status }: { status: string }): ReactNode {
  const key = (status ?? '').trim().toLowerCase()
  if (key === 'active') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-[3px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Active
      </span>
    )
  }
  if (key === 'ended' || key === 'closed') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-[3px] border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
        {key === 'closed' ? 'Closed' : 'Ended'}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
      {status}
    </span>
  )
}

interface InfoRowProps {
  icon: ReactNode
  label: string
  value: ReactNode
}

function InfoRow({ icon, label, value }: InfoRowProps): ReactNode {
  return (
    <div className="flex items-center justify-between border-b border-slate-50 py-2 text-sm last:border-0 last:pb-0">
      <div className="flex items-center gap-2 text-slate-500">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
      </div>
      <div className="truncate pl-2 text-right font-medium text-[#0f172a]">{value}</div>
    </div>
  )
}

export interface ChatDetailsPanelProps {
  entry: ChatListEntry
  onClose: () => void
  transfers: ChatTransfer[]
  onAssign?: () => void
  style?: CSSProperties
}

export default function ChatDetailsPanel({ entry, onClose, transfers, onAssign, style }: ChatDetailsPanelProps): ReactNode {
  const user = useAuthStore((s) => s.user)
  const chConfig = getChannelConfig(entry.channel)
  const ChIcon = chConfig.icon
  const toast = useToast()
  const { confirm, Dialog: ConfirmDialog } = useConfirmDialog()
  const { notes, loading: notesLoading, createNote, updateNote, creating, updating, deleteNote } = useChatNotes(entry.id, entry.channel)
  const { agents: assignableAgents } = useAssignableAgents(true)
  const { upload } = useUploadAttachment()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [notesPanelOpen, setNotesPanelOpen] = useState(false)
  const [noteFormMode, setNoteFormMode] = useState<'create' | 'view' | 'edit'>('create')
  const [selectedNote, setSelectedNote] = useState<ChatNote | null>(null)
  const [selfAssigning, setSelfAssigning] = useState(false)
  const [ending, setEnding] = useState(false)
  const [localStatus, setLocalStatus] = useState<string | null>(null)
  const [localAssignedAgentId, setLocalAssignedAgentId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [isPrivate, setIsPrivate] = useState(true)
  const [transferHistoryOpen, setTransferHistoryOpen] = useState(false)
  const [summaryOpen, setSummaryOpen] = useState(false)

  // Reset local status override when chat changes or entry updates
  useEffect(() => {
    setLocalStatus(null)
    setLocalAssignedAgentId(null)
  }, [entry.id, entry.status, entry.assignedAgentId])

  useEffect(() => {
    const socket = getSocket()
    const onResumed = (data: { chatId?: string | undefined; assignedAgentId?: string | undefined }): void => {
      if (data?.chatId === entry.id) {
        setLocalStatus('active')
        if (data.assignedAgentId) setLocalAssignedAgentId(data.assignedAgentId)
        onAssign?.()
      }
    }
    const onOfferAccepted = (data: { sessionId?: string | undefined; assignedAgentId?: string | undefined }): void => {
      if (data?.sessionId === entry.id) {
        setLocalStatus('active')
        if (data.assignedAgentId) setLocalAssignedAgentId(data.assignedAgentId)
        onAssign?.()
      }
    }
    const onEnded = (data: { chatId?: string | undefined }): void => {
      if (data?.chatId === entry.id) {
        setLocalStatus('abandoned')
      }
    }
    const onLocalResumed = (e: Event): void => {
      const detail = (e as CustomEvent<{ chatId?: string | undefined; assignedAgentId?: string | undefined }>).detail
      if (detail?.chatId === entry.id) {
        setLocalStatus('active')
        if (detail.assignedAgentId) setLocalAssignedAgentId(detail.assignedAgentId)
        onAssign?.()
      }
    }
    const onLocalEnded = (e: Event): void => {
      const detail = (e as CustomEvent<{ chatId?: string | undefined }>).detail
      if (detail?.chatId === entry.id) {
        setLocalStatus('abandoned')
      }
    }

    socket.on('live:chat:resumed', onResumed)
    socket.on('chat:offer:accepted', onOfferAccepted)
    socket.on('live:chat:ended', onEnded)
    window.addEventListener('kc3:chat:resumed', onLocalResumed)
    window.addEventListener('kc3:chat:ended', onLocalEnded)

    return () => {
      socket.off('live:chat:resumed', onResumed)
      socket.off('chat:offer:accepted', onOfferAccepted)
      socket.off('live:chat:ended', onEnded)
      window.removeEventListener('kc3:chat:resumed', onLocalResumed)
      window.removeEventListener('kc3:chat:ended', onLocalEnded)
    }
  }, [entry.id, onAssign])

  const currentStatus = localStatus ?? entry.status ?? 'active'
  const currentAssignedAgentId = localAssignedAgentId ?? entry.assignedAgentId

  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [attachmentIds, setAttachmentIds] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const openNoteForm = useCallback((mode: 'create' | 'view' | 'edit', note?: ChatNote) => {
    setNoteFormMode(mode)
    if (mode === 'create') {
      setSelectedNote(null)
      setDraft('')
      setIsPrivate(true)
      setPending([])
      setAttachmentIds([])
    } else if (note) {
      setSelectedNote(note)
      setDraft(note.bodyHtml ?? note.body)
      setIsPrivate(note.isPrivate)
      const existing: PendingAttachment[] = (note.attachments ?? []).map((att) => ({
        localId: crypto.randomUUID(),
        name: att.filename,
        state: 'uploaded',
        attachmentId: att.id,
        mimeType: att.mimeType,
        previewUrl: att.mimeType.startsWith('image/') ? att.url : undefined,
      }))
      setPending(existing)
      setAttachmentIds(note.attachments.map((a) => a.id))
    }
    setDrawerOpen(true)
  }, [])

  const handleSelfAssign = useCallback(() => {
    const apiBase = entry.channel === 'chat' ? 'live-chats' : `${entry.channel}-chats`
    if (!['live-chats', 'whatsapp-chats', 'facebook-chats', 'instagram-chats', 'telegram-chats'].includes(apiBase)) return
    setSelfAssigning(true)
    api
      .post<{ assignedAgentId: string; assignedAt: string }>(`/agent/${apiBase}/${entry.id}/assign`, {})
      .then(() => {
        toast.success('Chat assigned to you')
        onAssign?.()
      })
      .catch((err: unknown) => {
        const error = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Self-assign failed'
        toast.error(error)
      })
      .finally(() => setSelfAssigning(false))
  }, [entry.id, entry.channel, onAssign, toast])

  const handleEndChat = useCallback(() => {
    if (currentStatus !== 'active') return
    const apiBase = entry.channel === 'chat' ? 'live-chats' : `${entry.channel}-chats`
    setEnding(true)
    api
      .post(`/agent/${apiBase}/${entry.id}/end`, {})
      .then(() => {
        toast.success('Chat ended')
        setLocalStatus('abandoned')
        window.dispatchEvent(new CustomEvent('kc3:chat:ended', { detail: { chatId: entry.id } }))
        onAssign?.()
      })
      .catch((err: unknown) => {
        const error = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'End chat failed'
        toast.error(error)
      })
      .finally(() => setEnding(false))
  }, [entry.id, entry.channel, currentStatus, onAssign, toast])

  const handleViewNote = useCallback((note: ChatNote) => {
    openNoteForm('view', note)
  }, [openNoteForm])

  const handleEditNote = useCallback((note: ChatNote) => {
    openNoteForm('edit', note)
  }, [openNoteForm])

  const addFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return
    const list = Array.from(files)
    const additions: PendingAttachment[] = list.map((f) => ({
      localId: crypto.randomUUID(),
      name: f.name,
      state: 'uploading',
      mimeType: f.type || 'application/octet-stream',
      file: f,
    }))
    setPending((prev) => [...prev, ...additions])
    additions.forEach((a, i) => {
      const file = list[i]
      if (!file) return
      void (async () => {
        try {
          const res = await upload(file)
          const isImage = res.mimeType.startsWith('image/')
          setPending((prev) => prev.map((p) => p.localId === a.localId ? {
            ...p,
            state: 'uploaded',
            attachmentId: res.attachmentId,
            mimeType: res.mimeType,
            previewUrl: isImage ? `/api/attachments/${res.attachmentId}` : undefined,
          } : p))
          setAttachmentIds((prev) => [...prev, res.attachmentId])
        } catch (err) {
          console.error('[ChatDetailsPanel] Upload failed', { localId: a.localId, name: a.name, error: (err as Error)?.message })
          setPending((prev) => prev.map((p) => p.localId === a.localId ? { ...p, state: 'error' } : p))
        }
      })()
    })
  }

  const removeAttachment = (localId: string): void => {
    setPending((prev) => {
      const removed = prev.find((p) => p.localId === localId)
      if (removed?.attachmentId) setAttachmentIds((ids) => ids.filter((id) => id !== removed.attachmentId))
      return prev.filter((p) => p.localId !== localId)
    })
  }

  const uploading = pending.some((p) => p.state === 'uploading')

  const submit = useCallback(async (): Promise<void> => {
    if (isEmptyHtml(draft)) return
    const text = draft.replace(/<[^>]*>/g, '').trim()
    if (!text) return
    if (noteFormMode === 'create') {
      const note = await createNote({ body: text, bodyHtml: draft, isPrivate, attachments: attachmentIds })
      if (note) {
        toast.success('Note added')
        setDrawerOpen(false)
      } else {
        toast.error('Failed to add note')
      }
    } else if (noteFormMode === 'edit' && selectedNote) {
      const note = await updateNote(selectedNote.id, { body: text, bodyHtml: draft, isPrivate, attachments: attachmentIds })
      if (note) {
        toast.success('Note updated')
        setDrawerOpen(false)
      } else {
        toast.error('Failed to update note')
      }
    }
  }, [noteFormMode, selectedNote, draft, isPrivate, attachmentIds, createNote, updateNote, toast])

  const handleDeleteNote = useCallback(
    (noteId: string): void => {
      confirm({
        title: 'Delete note',
        message: 'Are you sure you want to delete this note?',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        onConfirm: () => {
          void deleteNote(noteId).then((success) => {
            if (success) {
              toast.success('Note deleted')
            } else {
              toast.error('Failed to delete note')
            }
          })
        },
      })
    },
    [confirm, deleteNote, toast],
  )

  const currentAssignedName = useMemo(() => {
    if (entry.aiAssigned) return entry.aiAgentName ?? 'AI Agent'
    if (!entry.assignedAgentId) return 'Unassigned'
    const agent = assignableAgents.find((a) => a.agentId === entry.assignedAgentId)
    return agent?.label ?? 'Unknown'
  }, [entry.aiAssigned, entry.aiAgentName, entry.assignedAgentId, assignableAgents])

  const assignedAt = useMemo(() => {
    if (entry.assignedAt) return entry.assignedAt
    const last = transfers[0]
    return last ? last.transferredAt : null
  }, [entry.assignedAt, transfers])

  return (
    <div style={style} className="hidden h-full w-96 shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-white xl:flex">
      <div className="shrink-0 flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Client Details</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Close details"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
        {/* Customer identity — matches the Client Info tab hero in conversation detail */}
        <div className={cn('overflow-hidden border border-slate-200 bg-white shadow-sm', R)}>
          <div className="flex flex-col gap-4 bg-gradient-to-r from-[#F5F7FF] via-white to-white p-5">
            <div className="flex items-start gap-4">
              <Avatar name={entry.customerName} size={60} circular />
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-bold text-[#0f172a]">{entry.customerName}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  {entry.customerEmail && (
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-slate-400" />
                      {entry.customerEmail}
                    </span>
                  )}
                  <span className={cn('inline-flex items-center gap-1 rounded-[3px] px-2 py-0.5 text-xs font-medium', chConfig.bg, chConfig.color)}>
                    <ChIcon className="h-3 w-3" />
                    {chConfig.label}
                  </span>
                </div>
                <p className="mt-1 truncate text-[11px] text-slate-400">{entry.customerHandle}</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <ClientStatus status={currentStatus} />
            </div>
          </div>
        </div>

        <Section title="Contact Info">
          <div className="space-y-0.5">
            <InfoRow
              icon={<Mail className="h-3.5 w-3.5 text-slate-400" />}
              label="Email"
              value={entry.customerEmail ?? '—'}
            />
            <InfoRow
              icon={<Hash className="h-3.5 w-3.5 text-slate-400" />}
              label="ID"
              value={entry.customerHandle}
            />
            <InfoRow
              icon={<User className="h-3.5 w-3.5 text-slate-400" />}
              label="Client"
              value={
                entry.clientId ? (
                  <Link
                    to={`/agent/clients?clientId=${encodeURIComponent(entry.clientId)}`}
                    className="text-[#3366FF] hover:underline"
                  >
                    View client
                  </Link>
                ) : (
                  '—'
                )
              }
            />
          </div>
        </Section>

        <Section title="Chat Info">
          <div className="space-y-0.5">
            <InfoRow
              icon={<Globe className="h-3.5 w-3.5 text-slate-400" />}
              label="Channel"
              value={chConfig.label}
            />
            <InfoRow
              icon={<Calendar className="h-3.5 w-3.5 text-slate-400" />}
              label="Started At"
              value={formatTime(entry.createdAt)}
            />
            <InfoRow
              icon={<MessageSquare className="h-3.5 w-3.5 text-slate-400" />}
              label="Last Message"
              value={formatTime(entry.timestamp)}
            />
            <InfoRow
              icon={<Activity className="h-3.5 w-3.5 text-slate-400" />}
              label="Status"
              value={<ClientStatus status={currentStatus} />}
            />
            <InfoRow
              icon={entry.aiAssigned ? <Bot className="h-3.5 w-3.5 text-purple-500" /> : <User className="h-3.5 w-3.5 text-slate-400" />}
              label="Assigned To"
              value={entry.aiAssigned ? <span className="text-purple-600">{currentAssignedName}</span> : currentAssignedName}
            />
            <InfoRow
              icon={<Clock className="h-3.5 w-3.5 text-slate-400" />}
              label="Assigned At"
              value={assignedAt ? formatDateTime(assignedAt) : '—'}
            />
          </div>
        </Section>

        {/* Telegram Group Info */}
        {entry.channel === 'telegram' && (entry.chatType === 'group' || entry.chatType === 'supergroup') && (
          <Section title="Group Info">
            <div className="space-y-2">
              <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{entry.customerName}</p>
                  <p className="text-xs text-slate-500">
                    {entry.chatType === 'supergroup' ? 'Supergroup' : 'Group'}
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-500 italic">
                Group member details are not available via Telegram Bot API. 
                Members can be seen in the chat thread by their message sender info.
              </p>
            </div>
          </Section>
        )}

        {transfers.length > 0 && (
          <Section
            title="Transfer"
            action={
              <GhostButton
                onClick={() => setTransferHistoryOpen(true)}
                className="px-2 py-1 text-[11px]"
              >
                View full history
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </GhostButton>
            }
          >
            {transfers[0] && (
              <div className="space-y-0.5">
                <InfoRow
                  icon={<ArrowRightLeft className="h-3.5 w-3.5 text-slate-400" />}
                  label="From"
                  value={transfers[0].fromAgentName ?? 'Unassigned'}
                />
                <InfoRow
                  icon={<User className="h-3.5 w-3.5 text-slate-400" />}
                  label="To"
                  value={transfers[0].toAgentName ?? 'Unassigned'}
                />
                <InfoRow
                  icon={<User className="h-3.5 w-3.5 text-slate-400" />}
                  label="By"
                  value={transfers[0].transferredByName ?? 'Unknown'}
                />
                <InfoRow
                  icon={<Activity className="h-3.5 w-3.5 text-slate-400" />}
                  label="Type"
                  value={transfers[0].transferType === 'automatic' ? 'System (agent unavailable)' : 'Manual'}
                />
                <InfoRow
                  icon={<Calendar className="h-3.5 w-3.5 text-slate-400" />}
                  label="Transferred"
                  value={formatDateTime(transfers[0].transferredAt)}
                />
                {transfers[0].reason && (
                  <InfoRow
                    icon={<FileText className="h-3.5 w-3.5 text-slate-400" />}
                    label="Reason"
                    value={transfers[0].reason}
                  />
                )}
                <InfoRow
                  icon={<Hash className="h-3.5 w-3.5 text-slate-400" />}
                  label="Total transfers"
                  value={transfers.length}
                />
              </div>
            )}
          </Section>
        )}

        <Section title="Tags">
          <TagsEditor chatId={entry.id} channel={entry.channel} />
        </Section>

        <Section
          title="Notes"
          action={
            <div className="flex items-center gap-2">
              {notes.length > 0 && (
                <GhostButton
                  onClick={() => setNotesPanelOpen(true)}
                  className="px-2 py-1 text-[11px]"
                >
                  See all
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </GhostButton>
              )}
            </div>
          }
        >
          {notesLoading ? (
            <div className="flex h-20 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
            </div>
          ) : notes.length === 0 ? (
            <p className="text-xs text-slate-400">No notes yet.</p>
          ) : (
            <NoteCard
              note={notes[0]!}
              canEdit={Boolean(notes[0]!.authorAgentId && notes[0]!.authorAgentId === user?.agentId)}
              canDelete={Boolean(notes[0]!.authorAgentId && notes[0]!.authorAgentId === user?.agentId)}
              compact
              onView={handleViewNote}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
            />
          )}
        </Section>

        <Section title="Quick Actions">
          <div className="flex flex-wrap gap-2">
            {((!entry.assignedAgentId && ['chat', 'whatsapp', 'facebook', 'instagram', 'telegram'].includes(entry.channel)) || (entry.aiAssigned && entry.assignedAgentId !== user?.agentId)) && (
              <PrimaryButton
                onClick={() => void handleSelfAssign()}
                disabled={selfAssigning}
                className="flex-1 justify-center py-1.5 text-[13px]"
              >
                <User className="mr-1.5 h-4 w-4" />
                {selfAssigning ? 'Assigning…' : 'Assign to me'}
              </PrimaryButton>
            )}
            {currentAssignedAgentId === user?.agentId && currentStatus === 'active' && (
              <DangerButton
                onClick={() => void handleEndChat()}
                disabled={ending}
                className="flex-1 justify-center py-1.5 text-[13px]"
              >
                <LogOut className="mr-1.5 h-4 w-4" />
                {ending ? 'Ending…' : 'End Chat'}
              </DangerButton>
            )}
            <GhostButton
              onClick={() => openNoteForm('create')}
              className="flex-1 justify-center py-1.5 text-[13px]"
            >
              <Pencil className="mr-1.5 h-4 w-4" />
              Add Notes
            </GhostButton>
            <GhostButton
              onClick={() => setSummaryOpen(true)}
              className="flex-1 justify-center py-1.5 text-[13px] border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <Sparkles className="mr-1.5 h-4 w-4 text-indigo-600" />
              Chat Summary
            </GhostButton>
          </div>
        </Section>
      </div>

      <ActionDrawer
        open={notesPanelOpen}
        onClose={() => setNotesPanelOpen(false)}
        title="Chat Notes"
        subtitle={`${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}
        icon={<MessageSquarePlus className="h-4 w-4" />}
        width={480}
      >
        <div className="space-y-4">
          <PrimaryButton
            onClick={() => {
              setNotesPanelOpen(false)
              openNoteForm('create')
            }}
            className="w-full justify-center"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Note
          </PrimaryButton>
          {notesLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  canEdit={Boolean(note.authorAgentId && note.authorAgentId === user?.agentId)}
                  canDelete={Boolean(note.authorAgentId && note.authorAgentId === user?.agentId)}
                  onView={handleViewNote}
                  onEdit={handleEditNote}
                  onDelete={handleDeleteNote}
                />
              ))}
            </div>
          )}
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={noteFormMode === 'create' ? 'Chat Notes' : 'Note'}
        subtitle={noteFormMode === 'create' ? 'Add an internal chat note' : (selectedNote ? (selectedNote.authorName ?? 'Unknown') : '')}
        icon={noteFormMode === 'create' ? <MessageSquarePlus className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        width={560}
        footer={
          <>
            <GhostButton onClick={() => setDrawerOpen(false)}>Cancel</GhostButton>
            <PrimaryButton
              onClick={() => void submit()}
              disabled={noteFormMode === 'view' || isEmptyHtml(draft) || creating || updating || uploading}
            >
              {creating || updating
                ? (noteFormMode === 'create' ? 'Saving…' : 'Updating…')
                : (noteFormMode === 'edit' ? 'Update' : 'Save')}
            </PrimaryButton>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Privacy">
            <button
              type="button"
              disabled={noteFormMode === 'view'}
              onClick={() => noteFormMode !== 'view' && setIsPrivate((v) => !v)}
              className={cn(
                'flex items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors',
                isPrivate
                  ? 'border-[#3366FF] bg-blue-50 text-[#3366FF]'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50',
                noteFormMode === 'view' ? 'cursor-not-allowed opacity-60' : '',
                R,
              )}
            >
              {isPrivate ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
              {isPrivate ? 'Private' : 'Public'}
            </button>
          </Field>
          <Field label="Note" required>
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              height={200}
              readOnly={noteFormMode === 'view'}
              placeholder={noteFormMode === 'view' ? 'Note content' : 'Write an internal note…'}
            />
          </Field>
          <Field label="Attachments">
            <div
              onClick={() => noteFormMode !== 'view' && fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault() }}
              onDrop={(e) => {
                e.preventDefault()
                if (noteFormMode !== 'view') {
                  addFiles(e.dataTransfer.files)
                }
              }}
              className={cn(
                'border-2 border-dashed border-slate-200 px-4 py-6 text-center text-xs text-slate-400 transition-colors',
                noteFormMode !== 'view' ? 'cursor-pointer hover:border-slate-300 hover:bg-slate-50' : 'cursor-not-allowed opacity-60',
                R,
              )}
            >
              <Paperclip className="mx-auto mb-2 h-5 w-5 text-slate-300" />
              Drag 'n' drop some files here, or click to select files.
              <div className="mt-1 text-[10px] text-slate-300">
                (Only {ACCEPTED_EXTENSIONS.replace(/\./g, '').replace(/,/g, ', ')} files will be accepted)
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPTED_EXTENSIONS}
              disabled={noteFormMode === 'view'}
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            {pending.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {pending.map((p) => (
                  <div
                    key={p.localId}
                    className={cn('flex items-center gap-2 border border-slate-100 px-3 py-2', R)}
                  >
                    {p.previewUrl ? (
                      <img src={p.previewUrl} alt={p.name} className="h-8 w-8 rounded object-cover" />
                    ) : (
                      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                    <span className="flex-1 truncate text-xs text-slate-600">{p.name}</span>
                    {p.state === 'uploading' && (
                      <span className="text-xs text-slate-400">Uploading…</span>
                    )}
                    {p.state === 'error' && (
                      <span className="text-xs text-red-500">Failed</span>
                    )}
                    <button
                      type="button"
                      disabled={noteFormMode === 'view'}
                      onClick={() => removeAttachment(p.localId)}
                      className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={transferHistoryOpen}
        onClose={() => setTransferHistoryOpen(false)}
        title="Transfer History"
        subtitle="Complete handoff and assignment chain"
        icon={<ArrowRightLeft className="h-4 w-4" />}
        width={640}
        footer={
          <GhostButton onClick={() => setTransferHistoryOpen(false)}>Close</GhostButton>
        }
      >
        <div className="space-y-5">
          <div className={`${R} border border-blue-100 bg-[#F6FAFF] p-4`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-600">Currently Handled By</p>
            <div className="mt-3 flex items-center gap-3">
              <Avatar name={currentAssignedName} size={48} circular />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold text-slate-900">{currentAssignedName}</p>
                  <span className={`inline-flex items-center ${R} bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700`}>
                    Current Agent
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-slate-500">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>Assigned on {assignedAt ? formatDateTime(assignedAt) : '—'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <ArrowRightLeft className="h-3.5 w-3.5" />
                <span>Total Transfers {transfers.length}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="mb-3 text-xs font-semibold text-slate-900">Handoff & Assignment Chain</h4>
            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="relative flex w-7 shrink-0 justify-center">
                  <div className="absolute top-3.5 h-full w-px bg-slate-200" />
                  <div className="relative z-10 mt-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white ring-4 ring-white">
                    <div className="h-2.5 w-2.5 rounded-full bg-white" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`mb-1 inline-flex items-center ${R} bg-green-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-700`}>
                    Current
                  </div>
                  <div className={`${R} border border-green-100 bg-green-50/40 p-3`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={currentAssignedName} size={36} circular colorClassName="bg-green-100 text-green-700" />
                        <div>
                          <p className="text-[13px] font-semibold text-slate-900">{currentAssignedName}</p>
                          <p className="text-[10px] text-slate-500">Assigned on {assignedAt ? formatDateTime(assignedAt) : '—'}</p>
                          <span className={`mt-1 inline-flex items-center ${R} bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700`}>
                            Current Agent
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-1.5 text-[10px] font-medium text-green-700">
                          <div className="h-2 w-2 rounded-full bg-green-500" />
                          Active Now
                        </div>
                        <p className="text-[10px] text-slate-500">Handling this chat</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {transfers.length === 0 ? (
                <p className="text-center text-xs text-slate-500">No transfers recorded for this chat.</p>
              ) : (
                transfers.map((t, idx) => (
                  <div key={t.id} className="flex gap-4">
                    <div className="relative flex w-7 shrink-0 justify-center">
                      <div className="absolute top-3.5 h-full w-px bg-slate-200" />
                      <div className="relative z-10 mt-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white ring-4 ring-white">
                        {idx + 1}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 text-[10px] font-semibold text-blue-700">Transfer {idx + 1}</div>
                      <div className={`${R} border border-slate-200 bg-white p-3`}>
                        <div className="flex items-center gap-3">
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <Avatar name={t.fromAgentName ?? 'Unassigned'} size={36} circular />
                            <div className="min-w-0">
                              <p className="truncate text-[11px] font-semibold text-slate-900">{t.fromAgentName ?? 'Unassigned'}</p>
                              <p className="text-[10px] text-slate-500">Previous Agent</p>
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-slate-400" />
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <Avatar name={t.toAgentName ?? 'Unassigned'} size={36} circular />
                            <div className="min-w-0">
                              <p className="truncate text-[11px] font-semibold text-slate-900">{t.toAgentName ?? 'Unassigned'}</p>
                              <p className="text-[9px] text-slate-500">New Agent</p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-slate-500">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDateTime(t.transferredAt)}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                          <span className={`rounded px-1.5 py-0.5 font-medium ${t.transferType === 'automatic' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                            {t.transferType === 'automatic' ? 'System' : 'Manual'}
                          </span>
                          <span className="text-slate-600">By: {t.transferredByName ?? 'Unknown'}</span>
                          {t.transferType === 'automatic' && t.toAgentName && (
                            <span className="text-amber-700">Accepted by {t.toAgentName}</span>
                          )}
                        </div>
                        {t.reason && (
                          <div className={`mt-2 ${R} border border-slate-200 bg-slate-50 p-2 text-[11px]`}>
                            <p className="text-[10px] font-medium text-slate-500">Reason</p>
                            <p className="text-slate-700">{t.reason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div className="flex gap-4">
                <div className="relative flex w-7 shrink-0 justify-center">
                  <div className="absolute top-3.5 h-full w-px bg-slate-200" />
                  <div className="relative z-10 mt-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-slate-200 text-slate-600 ring-4 ring-white">
                    <User className="h-4 w-4" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 text-[10px] font-semibold text-slate-600">Conversation Created</div>
                  <div className={`${R} border border-slate-200 bg-white p-3 text-[12px] text-slate-600`}>
                    <p>Visitor requested a human agent.</p>
                    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-500">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDateTime(entry.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ActionDrawer>

      <ChatSummaryDialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        chatId={entry.id}
        customerName={entry.customerName}
      />

      <ConfirmDialog />
    </div>
  )
}
